import React, { useMemo, useState } from "react";
import { TablePagination } from "@mui/material";
import { NavigateFunction } from "react-router-dom";
import { useUser } from "src/shared/context/UserContext";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "src/business-types/grocery/hooks/tableConfig";
import { LedgerDetails, LedgerOrderDetail, LedgerOrderHistory } from "src/shared/services/interfacev2";
import { splitDateTime } from "src/shared/components/dateTime";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";
import { useUserTheme } from "src/shared/hooks/useUserTheme";
import { formatNumber } from "src/business-types/laundry/modules/orders/OrderSearch";
import { useDateTimeFormatter } from "src/shared/configurations/hooks/useDateTimeFormatter";
import PrintConfigration, { ledgerPaid } from "src/print-configuration/PrintConfigration";

export type OrderType = LedgerOrderDetail | LedgerOrderHistory;

interface Props {
  ledger: OrderType[];
  page: number;
  rowsPerPage: number;
  paginationData: any;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  navigate: NavigateFunction;
  includeHistory: boolean;
  onSortChange: (field: string) => void;
  sortField: string;
  sortOrder: "asc" | "desc";
  customerData: LedgerDetails
}

const LedgerDetailTable: React.FC<Props> = ({
  ledger,
  page,
  rowsPerPage,
  paginationData,
  onPageChange,
  onRowsPerPageChange,
  navigate,
  includeHistory,
  onSortChange,
  sortField,
  sortOrder,
  customerData
}) => {
  const formatAmount = useFormatAmount();
  const { user, userConfigration } = useUser();
  const businessType = user?.businessType;
  const { bgClass, fontClass } = useUserTheme();
  const { formatDate, formatTime } = useDateTimeFormatter();
  const toBoolean = (value: any) => value === true || value === "true";
  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);
  const parcelFeeEnabled = toBoolean(userConfigration?.parcelFeeEnabled);
  const tipsEnabled = toBoolean(userConfigration.tipsEnabled);
  const orderIdEnabled = toBoolean(userConfigration?.orderIdEnabled);
  const billNoEnabled = toBoolean(userConfigration?.billNoEnabled);
  const [printData, setPrintData] = useState<ledgerPaid | null>()
  // Only show parcel fee and tips for Restaurant
  const showParcelFee = businessType === "Restaurant" && parcelFeeEnabled;
  const showTips = businessType === "Restaurant" && tipsEnabled;
  const baseColSpan = 7;
  const colSpanCount = baseColSpan + (deliveryFeeEnabled ? 1 : 0) + (showParcelFee ? 1 : 0) + (showTips ? 1 : 0) + (orderIdEnabled ? 1 : 0) + (billNoEnabled ? 1 : 0);

  // Map sortField from UI header to actual data key
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fieldKeyMap: Record<string, keyof LedgerOrderDetail | keyof LedgerOrderHistory> = {
    orderid: "orderId",
    billNumber: "billNumber",
    order_status: "status",
    orderedOn: "orderedOn",
    Orderamount: "orderAmount",
    discount: "discount",
    parcelFee: "parcelFee",
    tip: "tip",
    deliveryFee: "deliveryFee",
    Total_price: "finalAmount",
    ReceivedAmount: "receivedAmount",
    CurrentTotalRemaining: "outstandingBalance",
  };

  // Get orders to display - handle both LedgerOrderDetail and LedgerOrderHistory
  const ordersToDisplay = useMemo(() => {
    if (includeHistory) {
      // If includeHistory is true, we're dealing with LedgerOrderHistory items
      // which already contain the complete history structure
      return ledger;
    }
    // If includeHistory is false, we're dealing with LedgerOrderDetail items
    return ledger;
  }, [ledger, includeHistory]);

  // Sort and paginate
  const paginatedData = useMemo(() => {
    const key = fieldKeyMap[sortField] || sortField;

    const sortedOrders = [...ordersToDisplay].sort((a: OrderType, b: OrderType) => {
      const aVal = (a as any)[key];
      const bVal = (b as any)[key];

      // Handle date comparison
      if (key === "orderedOn") {
        const aDate = new Date(a.orderedOn?.$date || "");
        const bDate = new Date(b.orderedOn?.$date || "");
        return sortOrder === "asc" ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
      }

      // Handle numeric comparison
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }

      // Handle string comparison
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Fallback
      return 0;
    });

    if (rowsPerPage > 0) {
      return sortedOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }

    return sortedOrders;
  }, [fieldKeyMap, ordersToDisplay, page, rowsPerPage, sortField, sortOrder]);

  const totalRecords = ordersToDisplay.length;

  const handleSort = (field: string) => {
    onSortChange(field);
  };

  const renderSortArrow = (field: string) => {
    if (sortField !== field) return null;
    return sortOrder === "asc" ? " ↑" : " ↓";
  };

  // Helper function to get order ID display
  const getOrderIdDisplay = (order: LedgerOrderDetail | LedgerOrderHistory) => {
    if ("orderId" in order && order.orderId) return order.orderId;
    if ("paymentMethod" in order && order?.paymentMethod)
      return order?.paymentMethod || "N/A";
    // return order.billNumber?.toString() || "N/A";
  };

  // Navigation handler
  const handleNavigation = (order: OrderType) => {
    // const statusLower = order.status?.toLowerCase();
    const hasOrderId = !!order.orderId;
    // const isClickable = hasOrderId && statusLower !== "deleted";
    const isClickable = hasOrderId

    if (isClickable) {
      if (businessType === "Laundry") {
        navigate(`/v1/order-details/${order.orderId}`);
      } else if (businessType === "Saloon") {
        navigate(`/v1/saloon-order-display/${order.orderId}`);
      } else if (businessType === "Grocery") {
        navigate(`/v1/grocery-order-display/${order.orderId}`);
      } else if (businessType === "Restaurant") {
        navigate(`/v1/Respayment/${order.orderId}`);
      }
    }
  };

  const checkPayment = (order: OrderType) => {
    let formattedClientId = "0000";
    if (customerData?.customerDetails?.refId) {
      formattedClientId = String(customerData.customerDetails.refId).padStart(4, "0");
    }
    const customerName = [customerData?.customerDetails?.firstName, customerData?.customerDetails?.middleName, customerData?.customerDetails?.lastName].filter(Boolean).join(" ");
    const isAdvance = Number(order?.outstandingBalance ?? 0) < 0;
    const { date: paidDate, time: paidTime } = splitDateTime(order.orderedOn.$date);
    const osb = (order?.outstandingBalance)
    const refundAmount =
      order?.status === "Refund"
        ? Math.abs(Number(order?.receivedAmount || 0))
        : Number(order?.receivedAmount || 0);

    console.log(order)

    const prinLedger: ledgerPaid = {
      Balance: Number(osb ?? 0),
      Paid: Number(order?.receivedAmount ?? 0),
      PreviousBalance: Number(osb ?? 0) + Number(refundAmount) + Number(order?.discount ?? 0),
      previousLabel: isAdvance ? "Prev. Advance(السابق. يتقدم)" : "Prev. Balance(السابق. توازن)",
      paidLabel: order.status === "Adjusted" ? "Balance Paid(الرصيد المدفوع)" : isAdvance ? "Refund Amount(مبلغ الاسترداد)" : "Balance Paid(الرصيد المدفوع)",
      balanceLabel: isAdvance ? "Remaining Advance(التقدم المتبقي)" : "New Balance(توازن جديد)",
      isAdvance,
      billNo: order?.billNumber || 0,
      clientId: formattedClientId,
      CustomerPhon: Number(customerData?.customerDetails?.phones?.[0]?.phone || 0),
      CustomerName: customerName,
      Date: paidDate,
      Time: paidTime,
      paymentMethod: order?.paymentMethod ?? "",
      discount: order?.discount ?? 0,
      createdBy: order?.createdByName || "N/A",
    };
    setPrintData(prinLedger)

  }

  return (
    <div className={`w-[98%] mx-auto border border-gray-200 shadow-inner rounded-lg bg-white p-2 ${fontClass} overflow-hidden`}>
      <div className='overflow-y-auto'>
        <table className='min-w-full text-sm border-collapse'>
          <thead className={`${bgClass} text-white`}>
            <tr>
              <th className='p-4 text-left cursor-pointer' onClick={() => handleSort("orderid")}>Order ID{renderSortArrow("orderid")}</th>
              {billNoEnabled && (<th className='p-4 text-left cursor-pointer' onClick={() => handleSort("billNumber")}>Bill Number{renderSortArrow("billNumber")}</th>)}
              <th className='p-4 text-left cursor-pointer' onClick={() => handleSort("order_status")}>Order Status{renderSortArrow("order_status")}</th>
              <th className='p-4 text-left cursor-pointer' onClick={() => handleSort("orderedOn")}>Date & Time{renderSortArrow("orderedOn")}</th>
              <th className='p-4 text-right cursor-pointer' onClick={() => handleSort("Orderamount")}>Order Price (QAR){renderSortArrow("Orderamount")}</th>
              <th className='p-4 text-right cursor-pointer' onClick={() => handleSort("discount")}>Discount (QAR){renderSortArrow("discount")}</th>
              {deliveryFeeEnabled && (<th className='p-4 text-right cursor-pointer' onClick={() => handleSort("deliveryFee")}>Delivery Fee (QAR){renderSortArrow("deliveryFee")}</th>)}
              {showParcelFee && (<th className='p-4 text-right cursor-pointer' onClick={() => handleSort("parcelFee")}>parcel Fee (QAR){renderSortArrow("parcelFee")}</th>)}
              {showTips && (<th className='p-4 text-right cursor-pointer' onClick={() => handleSort("tip")}>Tip (QAR){renderSortArrow("tip")}</th>)}
              <th className='p-4 text-right cursor-pointer' onClick={() => handleSort("Total_price")}>Total Price (QAR){renderSortArrow("Total_price")}</th>
              <th className='p-4 text-right cursor-pointer' onClick={() => handleSort("ReceivedAmount")}>Received Price (QAR){renderSortArrow("ReceivedAmount")}</th>
              <th className='p-4 text-right cursor-pointer' onClick={() => handleSort("CurrentTotalRemaining")}>Remaining Price (QAR){renderSortArrow("CurrentTotalRemaining")}</th>
              <th className='p-4 text-right cursor-pointer'>Print</th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={colSpanCount} className="text-center py-4 text-gray-500">
                  No data available
                </td>
              </tr>
            ) : (
              paginatedData.map((order, index) => {
                const { date, time } = splitDateTime(order.orderedOn?.$date ?? "");
                const statusLower = order.status?.toLowerCase();
                const hasOrderId = !!order.orderId;
                // const isClickable = hasOrderId && statusLower !== "deleted";
                const isClickable = hasOrderId;
                // const orderIdColor = statusLower === "deleted" ? "text-gray-400" : hasOrderId ? "text-blue-600" : "text-black";
                const orderIdColor = hasOrderId ? "text-blue-600" : "text-black";
                const displayOrderId = getOrderIdDisplay(order);
                // const billNumberColor = statusLower === "deleted" ? "text-gray-400" : "text-blue-600";
                const billNumberColor = "text-blue-600";

                return (
                  <tr key={`${order._id?.$oid || index}`} className="border-t hover:bg-gray-50">
                      <td
                        className={`p-3 text-left ${orderIdColor} ${isClickable ? "cursor-pointer hover:underline" : ""}`}
                        onClick={() => handleNavigation(order)}
                      >
                        {displayOrderId}
                      </td>

                    {/* Bill Number Column - Made Clickable */}
                    {billNoEnabled && (
                      <td
                        className={`p-3 text-left ${billNumberColor} font-medium ${isClickable ? "cursor-pointer hover:underline" : ""}`}
                        onClick={() => isClickable && handleNavigation(order)}
                      >
                        {order?.billNumber ? formatNumber(order.billNumber) : "N/A"}
                      </td>
                    )}

                    {/* Order Status */}
                    <td
                      className="p-3 text-left"
                      style={{
                        color: order.status === "Cancelled" || statusLower === "deleted" ? "#ef4444" : "#3b82f6",
                        fontWeight: statusLower === "deleted" ? "normal" : "500"
                      }}
                    >
                      {order.status || "N/A"}
                    </td>

                    <td className="p-3 text-left">

                      {formatDate(date) && time ? `${formatDate(date)} / ${formatTime(time)}` : "N/A"}
                    </td>
                    <td className="p-3 text-right">{formatAmount(order.orderAmount)}</td>
                    <td className="p-3 text-right">{formatAmount(order.discount)}</td>
                    {deliveryFeeEnabled && (<td className="p-3 text-right">{formatAmount((order as any).deliveryFee || 0)}</td>)}
                    {showParcelFee && (<td className="p-3 text-right">{formatAmount((order as any).parcelFee || 0)}</td>)}
                    {showTips && (<td className="p-3 text-right">{formatAmount((order as any).tip || 0)}</td>)}
                    <td className="p-3 text-right">{formatAmount((order.finalAmount < 0) ? 0 : order.finalAmount)}</td>
                    <td className="p-3 text-right">{formatAmount(order.receivedAmount)}</td>
                    <td className="p-3 text-right"> {formatAmount(order.outstandingBalance)}</td>
                    <td className="p-3 text-right">
                      {order?.orderId ? <p className="font-bold text-4xl cursor-not-allowed text-gray-400">-</p> : <div onClick={() => checkPayment(order)} className="cursor-pointer">
                        🖨️
                      </div>}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className='border-t'>
        <TablePagination
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </div>
      {printData && <PrintConfigration ledgerPaid={printData} openPrint="true" isClosed={() => setPrintData(null)} />}
    </div>
  );
};

export default LedgerDetailTable;